import { useEffect, useState } from "react";
import { Download, Eye, EyeOff, ExternalLink, Lock, Pencil, QrCode, RefreshCw, Sparkles, Trash2, Upload, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import QrOverlayEditor from "@/components/admin/QrOverlayEditor";
import PlaceholdersManager from "@/components/admin/PlaceholdersManager";

const FILE_TYPES = [
  { v: "image", label: "Image (general)", category: "content_library", needsFile: true, bulk: true },
  { v: "banner", label: "Banner", category: "content_library", needsFile: true, bulk: true },
  { v: "template", label: "Template", category: "content_library", needsFile: true, bulk: true },
  { v: "video", label: "Video (Reel / YouTube)", category: "content_library", needsFile: false, supportsExternal: true },
  { v: "caption", label: "Caption template", category: "content_library", needsFile: false, isCaption: true },
  { v: "brand_logo", label: "Brand logo (PNG)", category: "brand_kit", needsFile: true, bulk: true },
  { v: "brand_color", label: "Brand color (hex)", category: "brand_kit", needsFile: false, isColor: true },
  { v: "brand_font", label: "Brand font", category: "brand_kit", needsFile: false, isFont: true },
];

const BLANK = {
  name: "", description: "", file_type: "image",
  category: "content_library", caption_text: "", external_url: "",
  hex: "", font_family: "", is_visible: true, protected: false,
};

const fileUrl = (path) => path ? `${API}/files/${path}` : null;

export default function AdminResourcesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [file, setFile] = useState(null);
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkMode, setBulkMode] = useState(false);
  const [filter, setFilter] = useState("all");
  const [seeding, setSeeding] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [maxUploadMb, setMaxUploadMb] = useState(25);
  const [preview, setPreview] = useState(null); // resource shown in preview modal
  const [editProposals, setEditProposals] = useState([]);

  const load = () => api.get("/admin/resources").then((r) => setItems(r.data.items));
  const loadEditProposals = () => api.get("/admin/resource-edits?status=pending").then((r) => setEditProposals(r.data.items || [])).catch(() => {});
  useEffect(() => { load(); loadEditProposals(); }, []);
  useEffect(() => {
    api.get("/admin/integrations").then((r) => setMaxUploadMb(r.data.max_upload_mb || 25)).catch(() => {});
  }, []);

  const typeDef = FILE_TYPES.find((t) => t.v === form.file_type) || FILE_TYPES[0];

  const setType = (v) => {
    const t = FILE_TYPES.find((x) => x.v === v);
    setForm({ ...form, file_type: v, category: t.category });
    setFile(null);
    setBulkFiles([]);
    // Switch off bulk mode if new type doesn't support it
    if (!t.bulk) setBulkMode(false);
  };

  const regenWordmark = async () => {
    if (!window.confirm("Re-generate the 'noobsync' wordmark logos with the corrected crop? This replaces the clipped wordmark assets (marks, favicons and banners are untouched).")) return;
    setRegenerating(true);
    try {
      const r = await api.post("/admin/resources/regenerate-wordmark");
      toast.success(r.data.message || "Wordmark logos regenerated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to regenerate wordmark");
    } finally {
      setRegenerating(false);
    }
  };

  const seedDefaults = async (force = false) => {
    const msg = force
      ? "FORCE RE-SEED: wipe ALL auto-generated brand-kit assets (standard + IdeaThon + HI) and rebuild them cleanly? Use this if a previous seed got stuck/partial. Your manually-uploaded resources are NOT touched. Runs ~60–120s."
      : "Seed the full NoobSync Brand Kit (logos, captions, colors, fonts)? This runs in the background and takes ~60–120 seconds.";
    if (!window.confirm(msg)) return;
    setSeeding(true);
    try {
      const res = await api.post(`/admin/resources/seed-brand-kit${force ? "?force=true" : ""}`);
      if (res.data.state === "done") {
        toast.success("Brand kit already seeded");
        load();
        setSeeding(false);
        return;
      }
      toast.info(force ? "Force re-seed started — rebuilding all brand-kit assets in the background." : "Seeding started — generating logos & captions in the background. This page will refresh automatically.");

      // Poll status every 4s, up to 5 minutes
      const start = Date.now();
      const poll = async () => {
        if (Date.now() - start > 5 * 60 * 1000) {
          toast.error("Seeding is taking longer than expected. Refresh manually in a minute.");
          setSeeding(false);
          return;
        }
        try {
          const s = await api.get("/admin/resources/seed-brand-kit/status");
          if (s.data.state === "done") {
            const r = s.data.result || {};
            const cf = r.colors_fonts || {};
            const lc = r.logos_captions || {};
            const ip = r.ideathon_pack || {};
            const hp = r.hi_pack || {};
            const totalCreated = (cf.created || 0) + (lc.created || 0) + (ip.created || 0) + (hp.created || 0);
            const totalSkipped = (cf.skipped || 0) + (lc.skipped || 0) + (ip.skipped || 0) + (hp.skipped || 0);
            toast.success(`Seed complete · ${totalCreated} added, ${totalSkipped} skipped`);
            load();
            setSeeding(false);
            return;
          }
          if (s.data.state === "error") {
            toast.error(`Seeding failed: ${s.data.error || "unknown error"}`);
            setSeeding(false);
            return;
          }
          setTimeout(poll, 4000);
        } catch (err) {
          setTimeout(poll, 4000); // network blip — keep trying
        }
      };
      setTimeout(poll, 4000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seeding failed");
      setSeeding(false);
    }
  };

  const bulkUpload = async () => {
    if (!bulkFiles.length) return toast.error("Select at least one file");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file_type", form.file_type);
      fd.append("category", form.category);
      fd.append("is_visible", form.is_visible);
      fd.append("protected", form.protected);
      if (form.description) fd.append("description", form.description);
      bulkFiles.forEach((f) => fd.append("files", f));
      const res = await api.post("/admin/resources/bulk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${res.data.count} file${res.data.count === 1 ? "" : "s"} uploaded`);
      setBulkFiles([]); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed");
    } finally {
      setUploading(false);
    }
  };

  const upload = async () => {
    if (bulkMode) return bulkUpload();
    if (!form.name) return toast.error("Name required");
    if (typeDef.needsFile && !file) return toast.error("File required");
    if (typeDef.isColor && !/^#?[0-9a-f]{6}$/i.test((form.hex || "").trim())) return toast.error("Enter a 6-digit hex like #A855F7");
    if (typeDef.isFont && !form.font_family.trim()) return toast.error("Font family required");
    if (form.file_type === "video" && !file && !form.external_url) return toast.error("Provide a file OR an external URL");

    const maxBytes = maxUploadMb * 1024 * 1024;
    if (file && file.size > maxBytes) {
      return toast.error(`File is ${(file.size / 1048576).toFixed(1)} MB — over the ${maxUploadMb} MB limit. Raise it under Integrations → Upload limit, or compress the file.`);
    }

    // Shared metadata fields (used by both single and chunked paths)
    const fields = {
      name: form.name,
      file_type: form.file_type,
      category: form.category,
      is_visible: form.is_visible,
      protected: form.protected,
    };
    if (form.description) fields.description = form.description;
    if (typeDef.isCaption) fields.caption_text = form.caption_text;
    if (typeDef.supportsExternal && form.external_url) fields.external_url = form.external_url;
    if (typeDef.isColor) fields.meta_json = JSON.stringify({ hex: form.hex.trim().replace(/^#?/, "#") });
    if (typeDef.isFont) fields.meta_json = JSON.stringify({ font_family: form.font_family.trim() });

    const CHUNK = 4 * 1024 * 1024; // 4MB chunks keep each request well under proxy limits
    setUploading(true);
    setProgress(0);
    try {
      if (file && file.size > CHUNK) {
        await chunkedUpload(file, fields, setProgress);
      } else {
        const fd = new FormData();
        Object.entries(fields).forEach(([k, v]) => fd.append(k, v));
        if (file) fd.append("file", file);
        await api.post("/admin/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Uploaded");
      setForm(BLANK); setFile(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  // Reliable large-file upload: split into chunks, post sequentially, then assemble server-side.
  const chunkedUpload = async (theFile, fields, onProgress) => {
    const CHUNK = 4 * 1024 * 1024;
    const rawId = (typeof crypto !== "undefined" && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const uploadId = rawId.replace(/[^A-Za-z0-9_-]/g, "");
    const total = Math.ceil(theFile.size / CHUNK);
    for (let i = 0; i < total; i++) {
      const blob = theFile.slice(i * CHUNK, Math.min(theFile.size, (i + 1) * CHUNK));
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("chunk_index", String(i));
      fd.append("total_chunks", String(total));
      fd.append("chunk", blob, theFile.name);
      await api.post("/admin/resources/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onProgress(Math.round(((i + 1) / total) * 95)); // leave headroom for the finalize step
    }
    await api.post("/admin/resources/chunk/complete", {
      upload_id: uploadId,
      total_chunks: total,
      filename: theFile.name,
      content_type: theFile.type || "",
      ...fields,
    });
    onProgress(100);
  };

  const toggle = async (id, v) => {
    await api.patch(`/admin/resources/${id}/visibility`, null, { params: { is_visible: v } });
    load();
  };
  const toggleProtected = async (id, v) => {
    if (!v && !window.confirm("Unprotect this resource? It will be deleted on the next 'Wipe launch data' operation.")) return;
    try {
      await api.patch(`/admin/resources/${id}/protected`, null, { params: { protected: v } });
      toast.success(v ? "🔒 Protected — survives DB wipes" : "Unprotected");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this resource?")) return;
    await api.delete(`/admin/resources/${id}`);
    load();
  };

  const [editing, setEditing] = useState(null); // resource being renamed
  const [editForm, setEditForm] = useState({ name: "", description: "", file_type: "" });
  const [customType, setCustomType] = useState(false);
  const [qrEditing, setQrEditing] = useState(null); // resource for QR overlay editor
  const [products, setProducts] = useState([]);
  const [customPh, setCustomPh] = useState([]);

  useEffect(() => {
    api.get("/referral-products").then((r) => setProducts(r.data.products || [])).catch(() => {});
    api.get("/admin/placeholders").then((r) => setCustomPh(r.data.items || [])).catch(() => {});
  }, []);

  const resolvePreview = (text) => {
    const tokens = { REF_LINK: "innovate.noobsync.com/?ref=NSA-YOURCODE", LINK: "innovate.noobsync.com/?ref=NSA-YOURCODE", REF: "NSA-YOURCODE", CODE: "NSA-YOURCODE", NAME: "Priya S." };
    (customPh || []).forEach((p) => { if (p.key) tokens[p.key.toUpperCase()] = p.value || `[${p.key}]`; });
    let out = text || "";
    Object.entries(tokens).forEach(([k, v]) => {
      out = out.replace(new RegExp(`\\{\\{\\s*${k}\\s*\\}\\}|\\{\\s*${k}\\s*\\}|\\[\\s*${k}\\s*\\]`, "gi"), v);
    });
    return out;
  };

  const EDIT_TYPE_PRESETS = ["image", "banner", "template", "video", "caption", "brand_logo", "brand_color", "brand_font"];

  const openEdit = (r) => {
    setEditForm({ name: r.name || "", description: r.description || "", file_type: r.file_type || "" });
    setCustomType(!!r.file_type && !EDIT_TYPE_PRESETS.includes(r.file_type));
    setEditing(r);
  };

  const saveEdit = async () => {
    if (!editForm.name.trim()) return toast.error("Name required");
    const isCommunity = editing.category === "community_originals";
    const payload = { name: editForm.name, description: editForm.description };
    if (editForm.file_type && editForm.file_type.trim()) payload.file_type = editForm.file_type.trim();
    try {
      if (isCommunity) {
        await api.patch(`/admin/resources/community/${editing.id}`, payload);
      } else {
        await api.patch(`/admin/resources/${editing.id}`, payload);
      }
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleCommunity = async (r, visible) => {
    try {
      await api.patch(`/admin/resources/community/${r.id}`, { is_visible: visible });
      toast.success(visible ? "Published to library" : "Hidden from library");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeCommunity = async (r) => {
    if (!window.confirm("Remove this community original from the library? The ambassador's submission and points are kept — it just won't appear in the resources list.")) return;
    try {
      await api.patch(`/admin/resources/community/${r.id}`, { removed: true });
      toast.success("Removed from library");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const publishWithConsent = async (r) => {
    if (!window.confirm(`"${r.creator_name || "This creator"}" didn't opt into public display when applying.\n\nOnly continue if you have their permission to share this publicly (e.g. it's your own team member). This will publish it to the ambassador library.`)) return;
    try {
      await api.patch(`/admin/resources/community/${r.id}`, { is_visible: true, force_consent: true });
      toast.success("Published to library (consent override)");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const [bulkBusy, setBulkBusy] = useState(false);

  const downloadSingle = async (r) => {
    // 1) Files in object storage → server with ?download=1 (forces attachment)
    if (r.file_path) {
      try {
        const url = `${API}/files/${r.file_path}?download=1`;
        const resp = await fetch(url, { credentials: "include" });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const blob = await resp.blob();
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = r.original_filename || `${(r.name || "resource").replace(/[^A-Za-z0-9._-]+/g, "_")}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
        return;
      } catch (e) {
        toast.error(`Download failed: ${e.message || e}`);
        return;
      }
    }

    // 2) Non-file resources — generate artifact client-side
    const baseName = (r.name || "resource").replace(/[^A-Za-z0-9._-]+/g, "_");
    const triggerBlob = (blob, ext) => {
      const a = document.createElement("a");
      const u = URL.createObjectURL(blob);
      a.href = u;
      a.download = `${baseName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    };

    if (r.file_type === "brand_color" && r.meta?.hex) {
      // Render a 512×512 swatch PNG with hex code overlaid
      const canvas = document.createElement("canvas");
      canvas.width = 512; canvas.height = 512;
      const ctx = canvas.getContext("2d");
      const hex = r.meta.hex.startsWith("#") ? r.meta.hex : `#${r.meta.hex}`;
      ctx.fillStyle = hex; ctx.fillRect(0, 0, 512, 512);
      // contrast text color
      const rgb = hex.match(/[\da-f]{2}/gi)?.map((h) => parseInt(h, 16)) || [0, 0, 0];
      const lum = (0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]) / 255;
      ctx.fillStyle = lum > 0.6 ? "#0a0a0a" : "#fafafa";
      ctx.font = "bold 28px sans-serif"; ctx.textAlign = "center";
      ctx.fillText(r.name || "color", 256, 440);
      ctx.font = "bold 48px monospace";
      ctx.fillText(hex.toUpperCase(), 256, 490);
      canvas.toBlob((blob) => blob && triggerBlob(blob, "png"));
      return;
    }
    if (r.file_type === "brand_font" && r.meta?.font_family) {
      const body = [
        r.name || "Brand font",
        `font-family: "${r.meta.font_family}"`,
        "",
        r.description || "",
        "",
        "CSS example:",
        `body { font-family: "${r.meta.font_family}", sans-serif; }`,
      ].join("\n");
      triggerBlob(new Blob([body], { type: "text/plain" }), "txt");
      return;
    }
    if (r.file_type === "caption" && r.caption_text) {
      const body = `${r.name || "Caption template"}\n${r.description || ""}\n\n${r.caption_text}`;
      triggerBlob(new Blob([body], { type: "text/plain" }), "txt");
      return;
    }
    if (r.external_url) {
      const body = `${r.name || ""}\n${r.external_url}\n${r.description || ""}`;
      triggerBlob(new Blob([body], { type: "text/plain" }), "url.txt");
      return;
    }
    toast.info("Nothing downloadable for this resource");
  };

  const bulkVisibility = async (visible) => {
    const targets = filtered.filter((r) => r.category !== "community_originals" && r.is_visible !== visible);
    if (!targets.length) {
      toast.info(visible ? "Everything in view is already published" : "Everything in view is already drafted");
      return;
    }
    if (!window.confirm(`${visible ? "Publish" : "Unpublish"} ${targets.length} resource${targets.length === 1 ? "" : "s"} in the current view (filter: ${filter.replace(/_/g, " ")})?`)) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/admin/resources/bulk-visibility", { ids: targets.map((r) => r.id), is_visible: visible });
      toast.success(`${visible ? "Published" : "Unpublished"} ${res.data.modified} of ${res.data.matched}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk action failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkDownload = async () => {
    const targets = filtered.filter((r) => r.category !== "community_originals");
    if (!targets.length) {
      toast.info("Nothing in this view to download");
      return;
    }
    if (targets.length > 200) {
      toast.error("Max 200 resources per ZIP — narrow the filter first");
      return;
    }
    setBulkBusy(true);
    try {
      const resp = await api.post("/admin/resources/download-zip", { ids: targets.map((r) => r.id) }, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `noobsync-${filter}-${targets.length}items.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${targets.length} resources as ZIP`);
    } catch (e) {
      toast.error("ZIP download failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const filtered = filter === "all"
    ? items
    : filter === "pending"
      ? items.filter((i) => i.pending_approval)
      : items.filter((i) => (i.category || "content_library") === filter && !i.pending_approval);
  const pendingCount = items.filter((i) => i.pending_approval).length;

  const approveResource = async (id, publish) => {
    try {
      await api.post(`/admin/resources/${id}/approve`, null, { params: { publish } });
      toast.success(publish ? "Approved & published" : "Approved as draft");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const supportsBulk = typeDef.bulk;

  const approveEdit = async (id) => {
    try {
      await api.post(`/admin/resource-edits/${id}/approve`);
      toast.success("Edit approved & applied");
      loadEditProposals();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const rejectEdit = async (id) => {
    if (!window.confirm("Reject this proposed edit? The live resource stays unchanged.")) return;
    try {
      await api.post(`/admin/resource-edits/${id}/reject`, {});
      toast.success("Edit rejected");
      loadEditProposals();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-5">
      <PlaceholdersManager onChange={setCustomPh} />

      {editProposals.length > 0 && (
        <div className="rounded-2xl border border-sky-500/30 bg-sky-500/[0.05] p-4 sm:p-6" data-testid="edit-requests-panel">
          <h3 className="flex items-center gap-2 font-display text-lg font-bold text-sky-200">
            <Pencil className="h-4 w-4" /> Content-manager edit requests ({editProposals.length})
          </h3>
          <p className="mt-1 text-xs text-sky-300/70">Proposed title/description changes to existing resources. The live version is unchanged until you approve.</p>
          <ul className="mt-4 space-y-3" data-testid="edit-requests-list">
            {editProposals.map((p) => {
              const nameChanged = (p.current_name || "") !== (p.proposed_name || "");
              const descChanged = (p.current_description || "") !== (p.proposed_description || "");
              return (
                <li key={p.id} data-testid={`edit-request-${p.id}`} className="rounded-xl border border-sky-500/20 bg-zinc-950 p-4">
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-zinc-500">
                    <span className="rounded bg-zinc-800 px-1.5 py-0.5">{(p.category || "").replace(/_/g, " ")}</span>
                    <span>{p.file_type}</span>
                    <span className="text-zinc-600">· proposed by {p.proposed_by_name || p.proposed_by}</span>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-zinc-800 bg-zinc-900/50 p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-zinc-500">Current</p>
                      <p className="mt-1 text-sm font-semibold text-zinc-300">{p.current_name || <span className="italic text-zinc-600">(no name)</span>}</p>
                      <p className="mt-1 whitespace-pre-wrap break-words text-xs text-zinc-500">{p.current_description || <span className="italic text-zinc-600">(no description)</span>}</p>
                    </div>
                    <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/[0.05] p-3">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-emerald-400/80">Proposed</p>
                      <p className={`mt-1 text-sm font-semibold ${nameChanged ? "text-emerald-300" : "text-zinc-300"}`}>{p.proposed_name || <span className="italic text-zinc-600">(no name)</span>}</p>
                      <p className={`mt-1 whitespace-pre-wrap break-words text-xs ${descChanged ? "text-emerald-200/90" : "text-zinc-500"}`}>{p.proposed_description || <span className="italic text-zinc-600">(no description)</span>}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {nameChanged && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">title changed</span>}
                    {descChanged && <span className="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-300">description changed</span>}
                    {!nameChanged && !descChanged && <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-bold text-zinc-400">no visible change</span>}
                    <div className="ml-auto flex gap-2">
                      <button onClick={() => approveEdit(p.id)} data-testid={`edit-request-approve-${p.id}`} className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/20">
                        <Eye className="h-3 w-3" /> Approve &amp; apply
                      </button>
                      <button onClick={() => rejectEdit(p.id)} data-testid={`edit-request-reject-${p.id}`} className="inline-flex items-center gap-1 rounded border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-[11px] font-bold text-rose-300 hover:bg-rose-500/20">
                        <Trash2 className="h-3 w-3" /> Reject
                      </button>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-display text-lg font-bold">Upload resource</h3>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={regenWordmark}
              disabled={regenerating}
              data-testid="regen-wordmark-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-amber-300 hover:bg-amber-500/20 disabled:opacity-60"
              title="Re-generates the 'noobsync' wordmark logos with the corrected crop (fixes the clipped text)"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${regenerating ? "animate-spin" : ""}`} /> {regenerating ? "Fixing…" : "Fix wordmark logos"}
            </button>
            <button
              onClick={() => seedDefaults(false)}
              disabled={seeding}
              data-testid="seed-brand-kit-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-[#A855F7]/40 bg-[#A855F7]/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-[#A855F7] hover:bg-[#A855F7]/20 disabled:opacity-60"
              title="Adds standard NoobSync brand colors + fonts in one click"
            >
              <Sparkles className="h-3.5 w-3.5" /> {seeding ? "Seeding…" : "Seed brand-kit defaults"}
            </button>
            <button
              onClick={() => seedDefaults(true)}
              disabled={seeding}
              data-testid="force-reseed-brand-kit-btn"
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-rose-300 hover:bg-rose-500/20 disabled:opacity-60"
              title="Wipes & rebuilds ALL auto-generated brand-kit assets — use if a seed got stuck or partial (your uploads are safe)"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${seeding ? "animate-spin" : ""}`} /> Force re-seed
            </button>
          </div>
        </div>        <div className="mt-4 space-y-3">
          <Select value={form.file_type} onValueChange={setType}>
            <SelectTrigger className="border-zinc-800 bg-zinc-900" data-testid="res-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Content library</div>
              {FILE_TYPES.filter((t) => t.category === "content_library").map((t) => (
                <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
              ))}
              <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#A855F7]">Brand kit</div>
              {FILE_TYPES.filter((t) => t.category === "brand_kit").map((t) => (
                <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-500">Goes into:</span>
            {(typeDef.isColor || typeDef.isFont) ? (
              <span className="rounded bg-[#A855F7]/20 px-2 py-0.5 font-bold text-[#A855F7]" title="Colors & fonts are always Brand Kit">Brand kit · locked</span>
            ) : (
              <div className="flex gap-1 rounded-md border border-zinc-800 bg-zinc-900/40 p-0.5">
                <button
                  type="button"
                  onClick={() => setForm({ ...form, category: "content_library" })}
                  data-testid="res-category-content"
                  className={`rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${form.category === "content_library" ? "bg-zinc-700 text-white" : "text-zinc-500 hover:text-zinc-300"}`}
                >Content library</button>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, category: "brand_kit" })}
                  data-testid="res-category-brand"
                  className={`rounded px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider ${form.category === "brand_kit" ? "bg-[#A855F7] text-white" : "text-zinc-500 hover:text-zinc-300"}`}
                >Brand kit</button>
              </div>
            )}
          </div>

          {supportsBulk && (
            <div className="flex items-center gap-2 rounded-md border border-zinc-800 bg-zinc-900/40 p-1">
              <button
                type="button"
                onClick={() => { setBulkMode(false); setBulkFiles([]); }}
                data-testid="res-mode-single"
                className={`flex-1 rounded px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider ${!bulkMode ? "bg-[#A855F7] text-white" : "text-zinc-400 hover:text-zinc-200"}`}
              >Single</button>
              <button
                type="button"
                onClick={() => { setBulkMode(true); setFile(null); }}
                data-testid="res-mode-bulk"
                className={`flex-1 rounded px-2.5 py-1.5 text-xs font-bold uppercase tracking-wider ${bulkMode ? "bg-[#A855F7] text-white" : "text-zinc-400 hover:text-zinc-200"}`}
              >Bulk · multi-file</button>
            </div>
          )}

          {!bulkMode && (
            <input data-testid="res-name" placeholder="Resource name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
          )}

          <textarea placeholder={bulkMode ? "Shared description for all files (optional)" : "Description (optional) — supports [REF_LINK], [CODE], [NAME] & your custom placeholders"} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
          {!bulkMode && form.description && /\[|\{/.test(form.description) && (
            <div className="rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] p-3" data-testid="desc-preview">
              <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-emerald-400/80">Live preview (what an ambassador sees)</p>
              <pre className="whitespace-pre-wrap break-words font-sans text-xs text-zinc-200">{resolvePreview(form.description)}</pre>
            </div>
          )}

          {!bulkMode && typeDef.isCaption && (
            <div className="space-y-2">
              <textarea data-testid="res-caption" placeholder="Caption text — use [REF_LINK] for the link, [CODE] for code, [NAME], or any custom placeholder you defined" value={form.caption_text} onChange={(e) => setForm({ ...form, caption_text: e.target.value })} rows={5} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-mono" />
              {(customPh.length > 0) && (
                <p className="text-[11px] text-zinc-500">Your custom placeholders: {customPh.map((p) => <span key={p.key} className="mr-1 rounded bg-zinc-800 px-1.5 py-0.5 font-mono text-[10px] text-zinc-300">[{p.key}]</span>)}</p>
              )}
              {form.caption_text && (
                <div className="rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] p-3" data-testid="caption-preview">
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-emerald-400/80">Live preview (what an ambassador sees)</p>
                  <pre className="whitespace-pre-wrap break-words font-sans text-xs text-zinc-200">{resolvePreview(form.caption_text)}</pre>
                </div>
              )}
            </div>
          )}

          {!bulkMode && typeDef.isColor && (
            <div className="flex items-center gap-3">
              <input data-testid="res-hex" placeholder="#A855F7" value={form.hex} onChange={(e) => setForm({ ...form, hex: e.target.value })} className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 font-mono text-sm" />
              <span className="h-10 w-10 rounded border border-zinc-800" style={{ backgroundColor: /^#?[0-9a-f]{6}$/i.test(form.hex) ? `#${form.hex.replace(/^#/, "")}` : "#000" }} />
            </div>
          )}

          {!bulkMode && typeDef.isFont && (
            <input data-testid="res-font" placeholder='font-family CSS value (e.g. "Space Grotesk")' value={form.font_family} onChange={(e) => setForm({ ...form, font_family: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
          )}

          {!bulkMode && typeDef.supportsExternal && (
            <input data-testid="res-external" placeholder="Original platform URL (Instagram Reel / YouTube link)" value={form.external_url} onChange={(e) => setForm({ ...form, external_url: e.target.value })} className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm" />
          )}

          {!bulkMode && (typeDef.needsFile || typeDef.supportsExternal) && (
            <div>
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">
                {typeDef.supportsExternal ? "File (optional if external URL provided)" : "File"}
              </label>
              <input data-testid="res-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-zinc-300 file:mr-3 file:rounded file:border-0 file:bg-[#A855F7] file:px-3 file:py-1 file:text-xs file:font-bold file:text-black" />
              <p className="mt-1 text-[11px] text-zinc-500">Max {maxUploadMb} MB. Large files upload in chunks for reliability. {file ? `Selected: ${(file.size / 1048576).toFixed(1)} MB` : ""}</p>
            </div>
          )}

          {bulkMode && supportsBulk && (
            <div className="rounded-md border border-dashed border-[#A855F7]/40 bg-[#A855F7]/5 p-3">
              <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-[#A855F7]">Select multiple files</label>
              <input
                data-testid="res-bulk-files"
                type="file"
                multiple
                onChange={(e) => setBulkFiles(Array.from(e.target.files || []))}
                className="block w-full text-sm text-zinc-300 file:mr-3 file:rounded file:border-0 file:bg-[#A855F7] file:px-3 file:py-1 file:text-xs file:font-bold file:text-black"
              />
              {bulkFiles.length > 0 && (
                <p className="mt-2 text-xs text-zinc-400">
                  <span className="font-bold text-[#A855F7]">{bulkFiles.length}</span> file{bulkFiles.length === 1 ? "" : "s"} selected:{" "}
                  <span className="text-zinc-500">{bulkFiles.slice(0, 3).map((f) => f.name).join(", ")}{bulkFiles.length > 3 ? ` +${bulkFiles.length - 3} more` : ""}</span>
                </p>
              )}
              <p className="mt-2 text-[11px] text-zinc-500">Each file becomes its own resource named after its filename.</p>
            </div>
          )}

          <label className="flex items-center gap-2 text-sm text-zinc-400">
            <input type="checkbox" checked={form.is_visible} onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} className="accent-[#A855F7]" />
            Visible to ambassadors
          </label>
          <label className="flex items-center gap-2 text-sm text-zinc-400" title="Protected resources survive 'Wipe launch data'. Use for evergreen brand assets that should never be lost during testing.">
            <input
              type="checkbox"
              checked={form.protected}
              onChange={(e) => setForm({ ...form, protected: e.target.checked })}
              className="accent-emerald-500"
              data-testid="res-protected-toggle"
            />
            🔒 Protect from auto-wipe
          </label>
          <button onClick={upload} disabled={uploading} className="inline-flex items-center gap-1.5 rounded-md bg-[#A855F7] px-5 py-2 text-sm font-bold text-white disabled:opacity-60" data-testid="res-upload-btn">
            {bulkMode ? <UploadCloud className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
            {uploading ? (progress > 0 ? `Uploading… ${progress}%` : "Uploading…") : (bulkMode ? `Upload ${bulkFiles.length || ""} files` : "Upload")}
          </button>
          {uploading && progress > 0 && (
            <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-zinc-800" data-testid="upload-progress">
              <div className="h-full rounded-full bg-[#A855F7] transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </div>

      <div className="rounded-2xl border border-zinc-900 bg-zinc-950 p-4 sm:p-6">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <h3 className="font-display text-lg font-bold">All resources ({filtered.length})</h3>
          <div className="flex flex-wrap gap-1 text-[10px] font-bold uppercase tracking-wider">
            {["all", "brand_kit", "content_library", "community_originals"].map((f) => (
              <button key={f} onClick={() => setFilter(f)} className={`rounded px-2 py-1 ${filter === f ? "bg-[#A855F7] text-white" : "text-zinc-500 hover:text-zinc-300"}`}>{f.replace(/_/g, " ")}</button>
            ))}
            <button onClick={() => setFilter("pending")} data-testid="res-filter-pending" className={`inline-flex items-center gap-1 rounded px-2 py-1 ${filter === "pending" ? "bg-amber-500 text-black" : "text-amber-400 hover:text-amber-300"}`}>
              pending{pendingCount > 0 ? ` (${pendingCount})` : ""}
            </button>
          </div>
        </div>

        {pendingCount > 0 && filter !== "pending" && (
          <button onClick={() => setFilter("pending")} data-testid="res-pending-banner" className="mt-3 flex w-full items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-left text-sm text-amber-200 hover:bg-amber-500/15">
            <span className="font-bold">{pendingCount}</span> content-manager upload{pendingCount === 1 ? "" : "s"} awaiting your approval — click to review.
          </button>
        )}

        {/* Bulk action toolbar — operates on the currently filtered view */}
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-zinc-800/80 bg-zinc-900/40 p-2">
          <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Bulk actions on {filter.replace(/_/g, " ")} ({filtered.length}):</span>
          <button
            onClick={() => bulkVisibility(true)}
            disabled={bulkBusy || !filtered.length}
            data-testid="res-bulk-publish-all"
            className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/20 disabled:opacity-50"
            title="Set is_visible=true on every resource in this view"
          >
            <Eye className="h-3 w-3" /> Publish all
          </button>
          <button
            onClick={() => bulkVisibility(false)}
            disabled={bulkBusy || !filtered.length}
            data-testid="res-bulk-unpublish-all"
            className="inline-flex items-center gap-1 rounded border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-[11px] font-bold text-amber-300 hover:bg-amber-500/20 disabled:opacity-50"
            title="Set is_visible=false on every resource in this view"
          >
            <EyeOff className="h-3 w-3" /> Unpublish all
          </button>
          <button
            onClick={bulkDownload}
            disabled={bulkBusy || !filtered.length}
            data-testid="res-bulk-download-all"
            className="inline-flex items-center gap-1 rounded border border-[#A855F7]/40 bg-[#A855F7]/10 px-2.5 py-1 text-[11px] font-bold text-[#A855F7] hover:bg-[#A855F7]/20 disabled:opacity-50"
            title="Bundle every resource in this view into a single ZIP and download"
          >
            <Download className="h-3 w-3" /> {bulkBusy ? "Working…" : "Download all (ZIP)"}
          </button>
        </div>
        <ul className="mt-4 max-h-[640px] space-y-2 overflow-y-auto" data-testid="res-list">
          {filtered.length === 0 && <li className="py-3 text-sm text-zinc-500">No resources uploaded yet.</li>}
          {filtered.map((r) => {
            const isImg = r.file_type === "brand_logo" || ["image", "logo", "banner", "template"].includes(r.file_type) || (r.content_type || "").startsWith("image/");
            const isCommunity = r.category === "community_originals";
            const previewSrc = isCommunity && r.file_path ? fileUrl(r.file_path) : null;
            return (
              <li key={r.id} data-testid={`admin-res-${r.id}`} className={`rounded border p-3 ${isCommunity ? "border-emerald-500/30 bg-emerald-500/[0.04]" : "border-zinc-800 bg-zinc-900"}`}>
                <div className="flex items-start gap-3">
                  {/* preview thumbnail */}
                  {r.file_type === "brand_color" ? (
                    <span className="h-12 w-12 shrink-0 rounded border border-zinc-700" style={{ backgroundColor: r.meta?.hex }} />
                  ) : r.file_type === "brand_font" ? (
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-zinc-700 bg-zinc-950 text-xs font-bold text-zinc-200" style={{ fontFamily: r.meta?.font_family }}>Aa</span>
                  ) : previewSrc ? (
                    <img src={previewSrc} alt="" className="h-12 w-12 shrink-0 rounded border border-zinc-700 bg-zinc-950 object-cover" />
                  ) : isImg && r.file_path ? (
                    <img src={fileUrl(r.file_path)} alt="" className="h-12 w-12 shrink-0 rounded border border-zinc-700 bg-white object-contain p-1" />
                  ) : r.file_type === "video" ? (
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-zinc-700 bg-zinc-950 text-xs text-[#A855F7]">VID</span>
                  ) : (
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded border border-zinc-700 bg-zinc-950 text-[10px] uppercase text-zinc-500">{r.file_type}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-display font-semibold text-white">
                      {!isCommunity ? (
                        <button
                          onClick={() => toggleProtected(r.id, !r.protected)}
                          className={`mr-1.5 inline-flex align-[-2px] rounded p-0.5 transition ${
                            r.protected
                              ? "text-emerald-400 hover:bg-emerald-500/10"
                              : "text-zinc-600 hover:text-zinc-400 hover:bg-zinc-800"
                          }`}
                          title={r.protected
                            ? "🔒 Protected — survives DB wipes. Click to unprotect."
                            : "Click to protect — survives DB wipes."}
                          data-testid={`admin-res-protect-${r.id}`}
                        >
                          <Lock className="h-3.5 w-3.5" />
                        </button>
                      ) : null}
                      {r.name}
                    </p>
                    <p className="text-xs text-zinc-500">
                      <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] ${
                        r.category === "brand_kit" ? "bg-[#A855F7]/20 text-[#A855F7]"
                          : isCommunity ? "bg-emerald-500/20 text-emerald-300"
                          : "bg-zinc-800 text-zinc-400"
                      }`}>{(r.category || "content_library").replace(/_/g, " ")}</span>
                      {r.file_type}
                      {!isCommunity && (
                        <span
                          className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                            r.is_visible
                              ? "bg-emerald-500/15 text-emerald-300"
                              : "bg-amber-500/15 text-amber-300"
                          }`}
                          title={r.is_visible ? "Published — ambassadors can see this" : "Draft — only admins can see this"}
                          data-testid={`admin-res-status-${r.id}`}
                        >
                          {r.is_visible ? "✓ published" : "⏳ draft"}
                        </span>
                      )}
                      {r.meta?.hex && <span className="ml-2 font-mono">{r.meta.hex}</span>}
                      {r.meta?.font_family && <span className="ml-2">{r.meta.font_family}</span>}
                      {!isCommunity && r.pending_approval && (
                        <span className="ml-2 rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold text-amber-300" data-testid={`admin-res-pending-${r.id}`}>⏳ pending approval</span>
                      )}
                      {!isCommunity && r.uploaded_by && (
                        <span className="ml-2 text-[10px] text-zinc-500">by {r.uploaded_by}</span>
                      )}
                      {isCommunity && (() => {
                        const live = r.is_visible && !r.pending_approval;
                        return (
                          <span
                            data-testid={`admin-res-live-${r.id}`}
                            title={live ? "Live — visible in the ambassador library" : "Draft — not visible to ambassadors yet"}
                            className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-bold ${live ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}
                          >
                            {live ? "✓ live" : "⏳ draft only"}
                          </span>
                        );
                      })()}
                      {isCommunity && !r.creator_consent && (
                        <span className="ml-2 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-300" title="Creator did not opt-in to public display — hidden from ambassador library">private</span>
                      )}
                    </p>
                    {r.external_url && <a href={r.external_url} target="_blank" rel="noreferrer" className="block truncate text-[11px] text-[#A855F7] hover:underline">{r.external_url}</a>}
                    {r.description && <p className="mt-1 text-xs text-zinc-500">{r.description}</p>}
                    {!isCommunity && r.pending_approval && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        <button onClick={() => approveResource(r.id, true)} data-testid={`res-approve-publish-${r.id}`} className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1 text-[11px] font-bold text-emerald-300 hover:bg-emerald-500/20">
                          <Eye className="h-3 w-3" /> Approve &amp; publish
                        </button>
                        <button onClick={() => approveResource(r.id, false)} data-testid={`res-approve-draft-${r.id}`} className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1 text-[11px] font-bold text-zinc-300 hover:bg-zinc-800">
                          Approve as draft
                        </button>
                        <span className="self-center text-[10px] text-zinc-600">Edit (✎) or reject (🗑) using the buttons →</span>
                      </div>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-1">
                    {isCommunity ? (
                      <>
                        {(r.external_url || r.file_path) && (
                          <a
                            href={r.external_url || fileUrl(r.file_path)}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                            title={r.external_url ? (r.is_published_live ? "Open live post" : "Open draft link") : "View post image"}
                            data-testid={`admin-res-open-${r.id}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        {r.file_path && (
                          <button
                            onClick={() => downloadSingle(r)}
                            className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                            title="Download proof image"
                            data-testid={`admin-res-download-${r.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(r)}
                          className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                          title="Edit name & description"
                          data-testid={`admin-res-edit-${r.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {r.creator_consent ? (
                          <button
                            onClick={() => toggleCommunity(r, !r.is_visible)}
                            className={`rounded p-1.5 ${r.is_visible ? "text-[#A855F7] hover:bg-[#A855F7]/10" : "text-zinc-500 hover:bg-zinc-800"}`}
                            title={r.is_visible ? "Visible to ambassadors — click to hide" : "Hidden — click to publish to library"}
                            data-testid={`admin-res-toggle-${r.id}`}
                          >
                            {r.is_visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                          </button>
                        ) : (
                          <button
                            onClick={() => publishWithConsent(r)}
                            className="rounded p-1.5 text-amber-400 hover:bg-amber-500/10"
                            title="Creator didn't opt into public display — click to publish with consent override (only if you have their permission)"
                            data-testid={`admin-res-locked-${r.id}`}
                          >
                            <Lock className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => removeCommunity(r)}
                          className="rounded p-1.5 text-rose-400 hover:bg-rose-500/10"
                          title="Remove from library (keeps submission & points)"
                          data-testid={`admin-res-remove-${r.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        {r.file_path ? (
                          <a
                            href={fileUrl(r.file_path)}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                            title="Open / preview in new tab"
                            data-testid={`admin-res-open-${r.id}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        ) : (
                          <button
                            onClick={() => setPreview(r)}
                            className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                            title="Preview"
                            data-testid={`admin-res-preview-${r.id}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        )}
                        {(r.file_path || r.file_type === "brand_color" || r.file_type === "brand_font" || r.file_type === "caption" || r.external_url) && (
                          <button
                            onClick={() => downloadSingle(r)}
                            className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                            title={
                              r.file_path ? "Download file"
                              : r.file_type === "brand_color" ? "Download as PNG color swatch"
                              : r.file_type === "brand_font" ? "Download as .txt font reference"
                              : r.file_type === "caption" ? "Download caption as .txt"
                              : "Download link as .txt"
                            }
                            data-testid={`admin-res-download-${r.id}`}
                          >
                            <Download className="h-4 w-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openEdit(r)}
                          className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
                          title="Edit name & description"
                          data-testid={`admin-res-edit-${r.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        {r.file_path && (
                          <button
                            onClick={() => setQrEditing(r)}
                            className={`rounded p-1.5 ${r.qr_overlay?.enabled ? "text-[#F97316] hover:bg-[#F97316]/10" : "text-zinc-400 hover:bg-zinc-800 hover:text-white"}`}
                            title={r.qr_overlay?.enabled ? "Personal-QR overlay ON — click to edit placement" : "Add personal-QR overlay for ambassadors"}
                            data-testid={`admin-res-qr-${r.id}`}
                          >
                            <QrCode className="h-4 w-4" />
                          </button>
                        )}
                        <button onClick={() => toggle(r.id, !r.is_visible)} className={`rounded p-1.5 ${r.is_visible ? "text-[#A855F7] hover:bg-[#A855F7]/10" : "text-zinc-500 hover:bg-zinc-800"}`} title={r.is_visible ? "Published — click to unpublish" : "Draft — click to publish to ambassadors"} data-testid={`admin-res-toggle-${r.id}`}>
                          {r.is_visible ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </button>
                        <button onClick={() => del(r.id)} className="rounded p-1.5 text-rose-400 hover:bg-rose-500/10" title="Delete" data-testid={`admin-res-delete-${r.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {qrEditing && (
        <QrOverlayEditor
          resource={qrEditing}
          products={products}
          onClose={() => setQrEditing(null)}
          onSaved={load}
        />
      )}

      {/* Edit name + description modal */}
      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setEditing(null)}
          data-testid="res-edit-modal"
        >
          <div
            className="relative w-full max-w-md rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setEditing(null)}
              className="absolute right-4 top-4 rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              data-testid="res-edit-close"
            >
              <span className="text-xl leading-none">×</span>
            </button>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              {editing.category?.replace(/_/g, " ")} · {editing.file_type}
            </p>
            <h3 className="mt-1 font-display text-xl font-bold text-white">Edit resource</h3>
            {editing.category === "community_originals" && (
              <p className="mt-1 text-[11px] text-emerald-300/80">Renaming a community original only affects how it shows in the library — the ambassador's original submission is untouched.</p>
            )}
            <div className="mt-4 space-y-3">
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Name</label>
                <input
                  data-testid="res-edit-name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
                  placeholder="Resource name"
                />
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Type / classification</label>
                <select
                  data-testid="res-edit-type"
                  value={customType ? "__custom__" : editForm.file_type}
                  onChange={(e) => {
                    if (e.target.value === "__custom__") {
                      setCustomType(true);
                      setEditForm({ ...editForm, file_type: "" });
                    } else {
                      setCustomType(false);
                      setEditForm({ ...editForm, file_type: e.target.value });
                    }
                  }}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm capitalize"
                >
                  {EDIT_TYPE_PRESETS.map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, " ")}</option>
                  ))}
                  <option value="__custom__">Custom…</option>
                </select>
                {customType && (
                  <input
                    data-testid="res-edit-type-custom"
                    value={editForm.file_type}
                    onChange={(e) => setEditForm({ ...editForm, file_type: e.target.value })}
                    className="mt-2 w-full rounded-md border border-[#A855F7]/40 bg-zinc-900 px-3 py-2 text-sm"
                    placeholder="Custom type (e.g. carousel, infographic, gif)"
                  />
                )}
                <p className="mt-1 text-[10px] text-zinc-600">Correct the type if the ambassador picked the wrong one, or enter a custom label.</p>
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-wide text-zinc-500">Description</label>
                <textarea
                  data-testid="res-edit-description"
                  value={editForm.description}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  rows={3}
                  className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm"
                  placeholder="Description (optional) — supports [REF_LINK], [CODE], [NAME] & custom placeholders"
                />
                {editForm.description && /\[|\{/.test(editForm.description) && (
                  <div className="mt-2 rounded-md border border-emerald-500/20 bg-emerald-500/[0.04] p-3" data-testid="res-edit-desc-preview">
                    <p className="mb-1 text-[10px] font-bold uppercase tracking-wide text-emerald-400/80">Live preview (what an ambassador sees)</p>
                    <pre className="whitespace-pre-wrap break-words font-sans text-xs text-zinc-200">{resolvePreview(editForm.description)}</pre>
                  </div>
                )}
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="rounded-md border border-zinc-800 px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-900" data-testid="res-edit-cancel">Cancel</button>
                <button onClick={saveEdit} className="rounded-md bg-[#A855F7] px-4 py-2 text-sm font-bold text-white hover:bg-[#9333EA]" data-testid="res-edit-save">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview modal for non-file resources (colors / fonts / captions) */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
          onClick={() => setPreview(null)}
          data-testid="res-preview-modal"
        >
          <div
            className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-zinc-800 bg-zinc-950 p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreview(null)}
              className="absolute right-4 top-4 rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-white"
              data-testid="res-preview-close"
            >
              <span className="text-xl leading-none">×</span>
            </button>
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">
              {preview.category?.replace(/_/g, " ")} · {preview.file_type}
            </p>
            <h3 className="mt-1 font-display text-2xl font-bold text-white">{preview.name}</h3>
            {preview.description && <p className="mt-1 text-sm text-zinc-400">{preview.description}</p>}

            <div className="mt-5">
              {preview.file_type === "brand_color" && preview.meta?.hex && (
                <div className="space-y-3">
                  <div
                    className="flex h-56 items-end justify-end rounded-lg border border-zinc-800 p-4"
                    style={{ backgroundColor: preview.meta.hex }}
                  >
                    <span
                      className="rounded bg-black/50 px-3 py-1 font-mono text-sm font-bold text-white backdrop-blur"
                    >
                      {preview.meta.hex.toUpperCase()}
                    </span>
                  </div>
                  <button
                    onClick={() => navigator.clipboard?.writeText(preview.meta.hex).then(() => toast.success("Hex copied"))}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-bold text-zinc-300 hover:bg-zinc-800"
                  >
                    Copy hex
                  </button>
                </div>
              )}

              {preview.file_type === "brand_font" && preview.meta?.font_family && (
                <div className="space-y-3">
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6">
                    <p style={{ fontFamily: preview.meta.font_family }} className="text-zinc-200">
                      <span className="block text-4xl font-bold">Aa Bb Cc 0123</span>
                      <span className="mt-3 block text-base">The quick brown fox jumps over the lazy dog.</span>
                      <span className="mt-1 block text-xs text-zinc-500">font-family: {preview.meta.font_family}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => navigator.clipboard?.writeText(preview.meta.font_family).then(() => toast.success("Font family copied"))}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-bold text-zinc-300 hover:bg-zinc-800"
                  >
                    Copy font-family
                  </button>
                </div>
              )}

              {preview.file_type === "caption" && preview.caption_text && (
                <div className="space-y-3">
                  <pre className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg border border-zinc-800 bg-zinc-900 p-4 font-mono text-sm text-zinc-200">
                    {preview.caption_text}
                  </pre>
                  <button
                    onClick={() => navigator.clipboard?.writeText(preview.caption_text).then(() => toast.success("Caption copied"))}
                    className="w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm font-bold text-zinc-300 hover:bg-zinc-800"
                  >
                    Copy caption
                  </button>
                </div>
              )}

              {preview.external_url && (
                <a
                  href={preview.external_url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-3 block truncate rounded-lg border border-zinc-800 bg-zinc-900 p-3 text-sm text-[#A855F7] hover:underline"
                >
                  {preview.external_url}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
